import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleCheckIcon, XIcon } from '@persona-id/design-tokens/icons';
import { sortBy } from 'lodash/fp';
import { FC, PropsWithChildren, useCallback, useState } from 'react';
import styled from 'styled-components';

import { CallerUIState } from 'dashboard/components/WorkflowModuleCallersList/WorkflowModuleCallersList';
import { CloseContainer, ModalBody, ModalContainer } from 'dashboard/layouts/ModalLayout';
import { WorkflowModule, WorkflowModulePropagationResult } from 'dashboard/models/workflow-module';
import PropagationModalContent from 'dashboard/views/DashboardWorkflowModulesShowView/PropagationModalContent';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Heading, Label, Span } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import useToggle from 'lib/hooks/useToggle';

const StyledModalContainer = styled(ModalContainer)`
  padding: 0;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing[2]} ${Spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.secondary};
`;

const Body = styled(ModalBody)`
  padding: ${Spacing[4]};
`;

const Footer = styled.footer`
  display: flex;
  justify-content: flex-end;
  padding: ${Spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`;

interface WorkflowModulePublishModalProps {
  description: string;
  workflowModule: WorkflowModule;
  cancelAction: () => void;
  confirmAction: (selectedWorkflows: string[]) => Promise<WorkflowModulePropagationResult[]>;
  setDescription: (val: string) => void;
}

const WorkflowModulePublishModal: FC<PropsWithChildren<WorkflowModulePublishModalProps>> = ({
  description,
  workflowModule,
  cancelAction,
  confirmAction,
  setDescription,
}) => {
  const callers = workflowModule.attributes.callers;
  const [callerUIStates, setCallerUIStates] = useState<CallerUIState[]>(
    sortBy(
      'id',
      callers.map(({ 'source-id': workflowId }) => ({
        id: workflowId,
        selected: true,
        disabled: false,
      })),
    ),
  );
  const [propagationResults, setPropagationResults] = useState<WorkflowModulePropagationResult[]>(
    [],
  );
  const [isPending, { enable, disable }] = useToggle(false);

  const handleConfirm = useCallback(async () => {
    enable();
    const selectedWorkflowIds = callers
      .filter((_, index) => callerUIStates[index].selected)
      .map(({ 'source-id': workflowId }) => workflowId);
    const results = await confirmAction(selectedWorkflowIds);
    setPropagationResults(results);
    disable();
  }, [confirmAction, disable, enable, callerUIStates, callers]);

  const onUpdateCallerUIState = useCallback((callerConfig, index) => {
    setCallerUIStates((currentConfigs) =>
      Object.assign([], currentConfigs, {
        [index]: callerConfig,
      }),
    );
  }, []);

  const cancelActionAndClearResults = useCallback(() => {
    cancelAction();
    setPropagationResults([]);
  }, [cancelAction]);

  if (propagationResults?.length) {
    return (
      <StyledModalContainer size='lg'>
        <Header>
          <FlexContainer>
            <CircleCheckIcon size={24} color={Color.PrimaryGreen} />
            <Spacer height={0} width={1} />
            <Heading size='sm'>Published</Heading>
          </FlexContainer>
          <CloseContainer
            aria-label='Close this modal'
            id='modal__close'
            onClick={isPending ? undefined : cancelActionAndClearResults}
            data-test='modal__close'
          >
            <XIcon />
          </CloseContainer>
        </Header>
        <Body>
          <PropagationModalContent
            callers={callers}
            callerUIStates={callerUIStates}
            propagationResults={propagationResults}
            onUpdateCallerUIState={onUpdateCallerUIState}
          />
        </Body>
        <Footer>
          <HStack justify='right'>
            <Button
              id='workflow-module-publish-modal__done'
              variant='primary'
              onClick={cancelActionAndClearResults}
            >
              Done
            </Button>
          </HStack>
        </Footer>
      </StyledModalContainer>
    );
  }

  return (
    <StyledModalContainer size='lg'>
      <Header>
        <Heading size='sm'>Publish Module</Heading>
        <CloseContainer
          aria-label='Close this modal'
          id='modal__close'
          onClick={isPending ? undefined : cancelAction}
          data-test='modal__close'
        >
          <XIcon />
        </CloseContainer>
      </Header>
      <Body>
        <Span>
          Publishing this module will make it available for use in any workflow through the Module
          step type.
        </Span>
        <Spacer height={2} />
        <Label size='md' weight='bold' htmlFor='workflow-module-publish-modal__description'>
          Description
        </Label>
        <Spacer height={1} />
        <TextArea
          id='workflow-module-publish-modal__description'
          value={description}
          placeholder='Optional description for this version...'
          onChange={(e) => setDescription(e.target.value)}
        />
        <Spacer height={2} />
        <PropagationModalContent
          callers={callers}
          callerUIStates={callerUIStates}
          onUpdateCallerUIState={onUpdateCallerUIState}
          propagationResults={[]}
        />
      </Body>
      <Footer>
        <HStack spacing={Spacing[4]} justify='right'>
          <Button
            id='workflow-module-publish-modal__cancel'
            variant='basic'
            onClick={cancelAction}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            id='workflow-module-publish-modal__confirm'
            variant='primary'
            onClick={handleConfirm}
            isPending={isPending}
            disabled={isPending}
          >
            Publish
          </Button>
        </HStack>
      </Footer>
    </StyledModalContainer>
  );
};

export default WorkflowModulePublishModal;
